/**
 * features/products/ProductFormDialog.tsx
 */

import { useEffect, useState } from 'react';
import { productInputSchema, type ProductInput } from '@ft/core';
import { ApiError } from '~/lib/api';
import type { Product } from '~/lib/types';
import { Dialog, useToast } from '~/ui/overlays';
import { Button, Field, Input, Textarea } from '~/ui/primitives';
import { useCreateProduct, useUpdateProduct } from './hooks';

type FormState = {
  code: string;
  name: string;
  description: string;
  price: string;
  unit: string;
};

const EMPTY: FormState = { code: '', name: '', description: '', price: '0', unit: 'ชิ้น' };

export function ProductFormDialog({
  open,
  onClose,
  product,
}: {
  open: boolean;
  onClose: () => void;
  product: Product | null;
}) {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<keyof ProductInput, string>>>({});
  const create = useCreateProduct();
  const update = useUpdateProduct();
  const toast = useToast();

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setForm(
      product
        ? {
            code: product.code,
            name: product.name,
            description: product.description,
            price: String(product.price),
            unit: product.unit,
          }
        : EMPTY,
    );
  }, [open, product]);

  const saving = create.isPending || update.isPending;

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function handleSubmit() {
    const input: ProductInput = {
      code: form.code,
      name: form.name,
      description: form.description,
      price: Number.parseFloat(form.price) || 0,
      unit: form.unit,
    };

    const parsed = productInputSchema.safeParse(input);
    if (!parsed.success) {
      const fieldErrors: Partial<Record<keyof ProductInput, string>> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof ProductInput;
        fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }

    try {
      if (product) {
        await update.mutateAsync({ id: product.id, input: parsed.data });
        toast.success('บันทึกข้อมูลสินค้าแล้ว');
      } else {
        await create.mutateAsync(parsed.data);
        toast.success('เพิ่มสินค้าใหม่แล้ว');
      }
      onClose();
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'บันทึกไม่สำเร็จ');
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={product ? 'แก้ไขสินค้า/บริการ' : 'เพิ่มสินค้า/บริการ'}
      footer={
        <>
          <Button onClick={onClose} disabled={saving}>
            ยกเลิก
          </Button>
          <Button variant="primary" onClick={() => void handleSubmit()} loading={saving}>
            บันทึก
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="ชื่อสินค้า/บริการ" required error={errors.name}>
          <Input
            value={form.name}
            onChange={(event) => setField('name', event.target.value)}
            invalid={Boolean(errors.name)}
            autoFocus
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="รหัสสินค้า (ถ้ามี)" error={errors.code}>
            <Input value={form.code} onChange={(event) => setField('code', event.target.value)} />
          </Field>
          <Field label="หน่วย" error={errors.unit}>
            <Input value={form.unit} onChange={(event) => setField('unit', event.target.value)} />
          </Field>
        </div>

        <Field label="ราคาต่อหน่วย (บาท)" required error={errors.price}>
          <Input
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            value={form.price}
            onChange={(event) => setField('price', event.target.value)}
            invalid={Boolean(errors.price)}
          />
        </Field>

        <Field label="รายละเอียด" error={errors.description}>
          <Textarea
            value={form.description}
            onChange={(event) => setField('description', event.target.value)}
          />
        </Field>
      </div>
    </Dialog>
  );
}
